/* Finance application wizard.
   Five-step flow: applicant → decision → gold collateral → guarantor
   cheques → repayment cheques → submit. Each step PUTs its slice
   to the server on Next, so closing the tab mid-flow only loses the
   in-progress current-step input. The wizard always works against a
   draft application; arg is either "new" (POST a fresh draft on first
   save) or an existing app id ("pf_…") to resume.

   Two independent invariants enforced before submit:
   • Σ guarantor.amount === approved amount
   • Σ repayment.amount === approved amount
   Server enforces the same.
*/
const { useState: useStateFW, useEffect: useEffectFW, useMemo: useMemoFW } = React;

function FinanceWizardPage({ arg, go }) {
  // arg === 'new' for a fresh wizard, otherwise the app id we're resuming.
  const isNew = arg === 'new';
  const [appId, setAppId] = useStateFW(isNew ? null : arg);
  const [app, setApp] = useStateFW(null); // last server-loaded snapshot
  const [step, setStep] = useStateFW(1);
  const [busy, setBusy] = useStateFW(false);
  const [err, setErr] = useStateFW('');
  const [info, setInfo] = useStateFW('');

  // Step 1 — applicant
  const [member, setMember] = useStateFW(null); // { itsNo, fullName, ... }
  const [purpose, setPurpose] = useStateFW('other');
  const [purposeOther, setPurposeOther] = useStateFW('');
  const [appliedAmount, setAppliedAmount] = useStateFW('');
  const [tenure, setTenure] = useStateFW('');
  const [notes, setNotes] = useStateFW('');

  // Step 2 — decision
  const [decision, setDecision] = useStateFW('approve'); // approve | reject
  const [approvedAmount, setApprovedAmount] = useStateFW('');
  const [reason, setReason] = useStateFW('');

  // Step 3 — gold items
  const blankGold = () => ({ karat: '', weight: '', description: '', estimatedValue: '' });
  const [goldItems, setGoldItems] = useStateFW([blankGold()]);

  // Step 4 — guarantor cheques
  // Bank is free-text (matches the repayment cheque row on step 5).
  const blankGuarantor = () => ({ guarantor: null, amount: '', chequeDate: '', chequeNumber: '', bank: '' });
  const [guarantors, setGuarantors] = useStateFW([blankGuarantor()]);

  // Step 5 — repayment cheques
  const blankRepayment = () => ({ issuerName: '', amount: '', dueDate: '', bank: '', cheque: '' });
  const [repayments, setRepayments] = useStateFW([blankRepayment()]);

  // Step 6 — approved-amount payment
  // Section A: fresh payouts from a company bank. Bank dropdown comes
  // from QH_DATA.banks. Reference is free-text (cheque # for cheques,
  // "transfer" for bank-transfer payouts, "cash" for cash).
  const blankPayout = () => ({ bankId: '', reference: '', issueDate: '', description: '', amount: '' });
  const [payouts, setPayouts] = useStateFW([]);
  // Section B: rotated repayment cheques (selected from the available
  // pool). Each row stores the source repayment id + denormalised
  // metadata so totals + display don't need another fetch.
  const [rotations, setRotations] = useStateFW([]); // [{ sourceRepaymentId, applicant, cheque, bank, dueDate, amount, rotatedOn }]
  // Available rotation pool, fetched on entering step 6.
  const [rotationPool, setRotationPool] = useStateFW([]);

  // Resume an existing draft / app: fetch + hydrate every step's state.
  useEffectFW(() => {
    if (isNew) return;
    let cancel = false;
    setBusy(true); setErr('');
    window.QH_API.get(`/finance/applications/${arg}`)
      .then(async a => {
        if (cancel) return;
        setApp(a);
        setAppId(a.id);
        if (a.memberItsNo) {
          try {
            const m = await window.QH_API.get(`/members/${a.memberItsNo}`);
            if (!cancel) setMember(m);
          } catch {}
        }
        // Legacy 'house' is normalised to 'housing' by the server-side
        // migration, but be defensive in case an older draft is loaded.
        setPurpose(a.purpose === 'house' ? 'housing' : (a.purpose || 'other'));
        setPurposeOther(a.purposeOther || '');
        setAppliedAmount(a.appliedAmount > 0 ? String(a.appliedAmount) : '');
        setTenure(a.tenure > 0 ? String(a.tenure) : '');
        setNotes(a.notes || '');
        setApprovedAmount(a.amount > 0 ? String(a.amount) : '');
        setDecision(a.status === 'rejected' ? 'reject' : 'approve');
        setReason(a.reason || '');
        setGoldItems((a.goldItems && a.goldItems.length)
          ? a.goldItems.map(g => ({
              karat: g.karat || '', weight: String(g.weightGrams ?? ''),
              description: g.description || '',
              estimatedValue: g.estimatedValue != null ? String(g.estimatedValue) : '',
            }))
          : [blankGold()]);
        setGuarantors((a.guarantorCheques && a.guarantorCheques.length)
          ? await Promise.all(a.guarantorCheques.map(async c => {
              let g = null;
              if (c.guarantorItsNo) {
                try { g = await window.QH_API.get(`/members/${c.guarantorItsNo}`); } catch {}
              }
              if (!g) g = { itsNo: c.guarantorItsNo, fullName: c.guarantorName };
              return {
                guarantor: g,
                amount: String(c.amount ?? ''),
                chequeDate: (c.chequeDate || '').slice(0, 10),
                chequeNumber: c.chequeNumber || '',
                bank: c.bank || '',
              };
            }))
          : [blankGuarantor()]);
        setRepayments((a.repayments && a.repayments.length)
          ? a.repayments.map(r => ({
              issuerName: r.issuerName || '',
              amount: String(r.amount ?? ''),
              dueDate: (r.dueDate || '').slice(0, 10),
              bank: r.bank || '',
              cheque: r.cheque || '',
            }))
          : [blankRepayment()]);
        setPayouts((a.disbursementPayouts || []).map(p => ({
          id: p.id, bankId: p.bankId, reference: p.reference || '',
          issueDate: (p.issueDate || '').slice(0, 10),
          description: p.description || '', amount: String(p.amount ?? ''),
        })));
        setRotations((a.disbursementRotations || []).map(r => ({
          sourceRepaymentId: r.sourceRepaymentId,
          applicant: r.sourceApplicant, cheque: r.sourceCheque,
          bank: r.sourceBank, dueDate: r.sourceDueDate,
          amount: Number(r.amount), rotatedOn: (r.rotatedOn || '').slice(0, 10),
        })));
      })
      .catch(e => { if (!cancel) setErr(e.message || 'Failed to load application.'); })
      .finally(() => { if (!cancel) setBusy(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arg]);

  // Track totals live so the submit button can light up only when both
  // independent constraints are satisfied.
  const approvedNum = Number(approvedAmount) || 0;
  const guarantorTotal = guarantors.reduce((s, g) => s + (Number(g.amount) || 0), 0);
  const repaymentTotal = repayments.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const payoutTotal = payouts.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const rotationTotal = rotations.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const paymentTotal = payoutTotal + rotationTotal;
  const eps = 0.001;
  const guarantorMatches = approvedNum > 0 && Math.abs(guarantorTotal - approvedNum) <= eps;
  const repaymentMatches = approvedNum > 0 && Math.abs(repaymentTotal - approvedNum) <= eps;
  const paymentMatches = approvedNum > 0 && Math.abs(paymentTotal - approvedNum) <= eps;

  const canSubmit = decision === 'reject'
    ? !!reason.trim()
    : (approvedNum > 0 && guarantorMatches && repaymentMatches && paymentMatches
        && goldItems.some(g => Number(g.weight) > 0));

  // Persist a draft to the server, creating one on first save when arg=new.
  const ensureDraft = async () => {
    if (appId) return appId;
    const created = await window.QH_API.post('/finance/applications/draft', {
      memberItsNo: member?.itsNo ?? null,
      applicant: member?.fullName || null,
      purpose,
      purposeOther: purpose === 'other' ? (purposeOther || null) : null,
      appliedAmount: Number(appliedAmount) || 0,
      tenure: Number(tenure) || 0,
      notes: notes || null,
    });
    setAppId(created.id);
    return created.id;
  };

  const saveStep1 = async () => {
    const id = await ensureDraft();
    await window.QH_API.put(`/finance/applications/${id}`, {
      memberItsNo: member?.itsNo ?? null,
      applicant: member?.fullName || null,
      purpose,
      purposeOther: purpose === 'other' ? (purposeOther || '') : '',
      appliedAmount: Number(appliedAmount) || 0,
      tenure: Number(tenure) || 0,
      notes: notes || null,
    });
  };
  const saveStep2 = async () => {
    const id = await ensureDraft();
    await window.QH_API.put(`/finance/applications/${id}`, {
      approvedAmount: Number(approvedAmount) || 0,
      reason: decision === 'reject' ? reason : null,
      decision,
    });
  };
  const saveStep3 = async () => {
    const id = await ensureDraft();
    const items = goldItems
      .filter(g => g.karat.trim() && Number(g.weight) > 0)
      .map(g => ({
        karat: g.karat.trim(),
        weightGrams: Number(g.weight),
        description: g.description?.trim() || null,
        estimatedValue: g.estimatedValue ? Number(g.estimatedValue) : null,
      }));
    await window.QH_API.put(`/finance/applications/${id}/gold`, items);
  };
  const saveStep4 = async () => {
    const id = await ensureDraft();
    const items = guarantors
      .filter(g => g.guarantor?.fullName && Number(g.amount) > 0)
      .map(g => ({
        guarantorName: g.guarantor.fullName,
        guarantorItsNo: g.guarantor.itsNo ?? null,
        amount: Number(g.amount),
        chequeDate: g.chequeDate || new Date().toISOString().slice(0, 10),
        chequeNumber: g.chequeNumber || null,
        guarantorBankId: null,
        bank: g.bank?.trim() || null,
      }));
    await window.QH_API.put(`/finance/applications/${id}/guarantors`, items);
  };
  const saveStep5 = async () => {
    const id = await ensureDraft();
    const items = repayments
      .filter(r => Number(r.amount) > 0 && r.cheque?.trim())
      .map(r => ({
        issuerName: r.issuerName?.trim() || member?.fullName || null,
        amount: Number(r.amount),
        dueDate: r.dueDate || new Date().toISOString().slice(0, 10),
        bank: r.bank.trim(),
        cheque: r.cheque.trim(),
      }));
    await window.QH_API.put(`/finance/applications/${id}/repayments`, items);
  };
  const saveStep6 = async () => {
    const id = await ensureDraft();
    const today = new Date().toISOString().slice(0, 10);
    const body = {
      payouts: payouts
        .filter(p => p.bankId && Number(p.amount) > 0)
        .map(p => ({
          bankId: p.bankId,
          reference: p.reference?.trim() || null,
          issueDate: p.issueDate || null,
          description: p.description?.trim() || null,
          amount: Number(p.amount),
        })),
      rotations: rotations.map(r => ({
        sourceRepaymentId: r.sourceRepaymentId,
        rotatedOn: r.rotatedOn || today,
      })),
    };
    await window.QH_API.put(`/finance/applications/${id}/payment`, body);
  };

  const saveCurrentStep = async () => {
    if (step === 1) await saveStep1();
    else if (step === 2) await saveStep2();
    else if (step === 3) await saveStep3();
    else if (step === 4) await saveStep4();
    else if (step === 5) await saveStep5();
    else if (step === 6) await saveStep6();
    // Step 7 (documents) needs no per-step PUT — uploads happen in
    // real time inside the DocumentAttachments widget.
  };

  // Soft validation gates between steps. Submit-time hard validation
  // happens at /submit and is reflected in canSubmit above.
  const canAdvance = () => {
    if (step === 1) {
      if (!member?.itsNo) return 'Pick a member to start the application.';
      if (!(Number(appliedAmount) > 0)) return 'Applied amount must be greater than zero.';
      if (!(Number(tenure) >= 1)) return 'Tenure must be at least one month.';
      return null;
    }
    if (step === 2) {
      if (decision === 'approve' && !(Number(approvedAmount) > 0)) return 'Approved amount must be greater than zero.';
      if (decision === 'reject' && !reason.trim()) return 'Reason is required to reject.';
      return null;
    }
    if (step === 3) {
      if (decision === 'reject') return null;
      if (!goldItems.some(g => Number(g.weight) > 0)) return 'Add at least one gold item with weight.';
      return null;
    }
    if (step === 4) {
      if (decision === 'reject') return null;
      if (!guarantors.some(g => g.guarantor?.itsNo && Number(g.amount) > 0)) return 'Add at least one guarantor cheque.';
      if (!guarantorMatches) return `Guarantor cheque total must equal the approved amount (${approvedNum.toFixed(3)} BHD).`;
      // Block self-guarantor at the row level too — server enforces but
      // a client-side check is friendlier.
      const selfRow = guarantors.find(g => g.guarantor?.itsNo === member?.itsNo);
      if (selfRow) return 'Applicant cannot be their own guarantor.';
      return null;
    }
    if (step === 5) {
      if (decision === 'reject') return null;
      if (!repaymentMatches) return `Repayment cheque total must equal the approved amount (${approvedNum.toFixed(3)} BHD).`;
      return null;
    }
    if (step === 6) {
      if (decision === 'reject') return null;
      if (!paymentMatches) return `Approved payment total must equal the approved amount (${approvedNum.toFixed(3)} BHD). Bank payouts ${payoutTotal.toFixed(3)} + rotated ${rotationTotal.toFixed(3)} = ${paymentTotal.toFixed(3)}.`;
      return null;
    }
    return null;
  };

  const onNext = async () => {
    setErr(''); setInfo('');
    const err = canAdvance();
    if (err) { setErr(err); return; }
    setBusy(true);
    try {
      await saveCurrentStep();
      // Refresh the global store so the Finance Apps list reflects this
      // step's persisted state if the operator exits the wizard mid-flow.
      window.QH_STORE?.reload?.().catch(() => {});
      // On reject, jump straight from step 2 to submit (steps 3-6 are
      // skipped — no security, payment plan, or scheduling for a reject).
      if (step === 2 && decision === 'reject') setStep(7);
      else setStep(s => Math.min(7, s + 1));
    } catch (e) { setErr(e.message || 'Save failed.'); }
    finally { setBusy(false); }
  };
  const onBack = () => {
    setErr(''); setInfo('');
    setStep(s => Math.max(1, s - 1));
  };
  const onSaveDraft = async () => {
    setErr(''); setInfo('');
    setBusy(true);
    try {
      await saveCurrentStep();
      // Refresh the global store so the Finance Apps list shows the
      // newly-created (or updated) draft when the operator clicks
      // "Back to list". Without this, the list renders from the cached
      // QH_DATA.financeApps that doesn't yet include this draft and the
      // operator thinks their work was lost.
      window.QH_STORE?.reload?.().catch(() => {});
      setInfo('Draft saved. You can resume later from the Finance Apps list.');
    } catch (e) { setErr(e.message || 'Save failed.'); }
    finally { setBusy(false); }
  };
  const onSubmit = async () => {
    setErr(''); setInfo('');
    setBusy(true);
    try {
      // Final flush of the payment plan — saveStep6 also runs server-side
      // validations (totals, rotation pool freshness, etc.). For rejected
      // applications we skip — the payment plan isn't required.
      if (decision !== 'reject') await saveStep6();
      const id = appId;
      await window.QH_API.post(`/finance/applications/${id}/submit`, {
        approvedAmount: Number(approvedAmount) || undefined,
        reason: decision === 'reject' ? reason : null,
        decision,
      });
      window.QH_STORE?.reload?.();
      setInfo(decision === 'reject' ? 'Application rejected.' : 'Application approved.');
      // Bounce back to the finance list after a short delay.
      setTimeout(() => go && go('finance'), 800);
    } catch (e) { setErr(e.message || 'Submit failed.'); }
    finally { setBusy(false); }
  };

  if (busy && !app && !isNew) return <div className="page-body"><div style={{padding:24,color:'var(--ink-3)'}}>Loading…</div></div>;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <span className="clickable" onClick={() => go('finance')} style={{cursor:'pointer'}}>Finance Apps</span>
            <I.ChevronRight s={10}/>
            <span>{isNew ? 'New application' : (appId || 'New application')}</span>
          </div>
          <h1>Finance application {appId && <span className="font-mono muted text-xs" style={{marginLeft:8}}>{appId}</span>}</h1>
          <div className="subtitle">
            5-step wizard · drafts auto-save on Next · resume any time from the Finance Apps list
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn ghost" onClick={() => {
            // Refresh first so the list reflects any auto-saved draft
            // changes from the wizard's PUTs.
            window.QH_STORE?.reload?.().catch(() => {});
            go('finance');
          }}><I.ArrowRight s={11} style={{transform:'rotate(180deg)'}}/> Back to list</button>
        </div>
      </div>

      <div className="page-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        <div className="stepper">
          {['Applicant', 'Decision', 'Gold security', 'Guarantor cheques', 'Repayment cheques', 'Approved payment', 'Attach documents'].map((lbl, i) => {
            const num = i + 1;
            return (
              <React.Fragment key={num}>
                <div className={`step ${step === num ? 'active' : step > num ? 'done' : ''}`}>
                  <span className="step-num">{step > num ? <I.Check s={11}/> : num}</span>{lbl}
                </div>
                {i < 6 && <div className="step-line"/>}
              </React.Fragment>
            );
          })}
        </div>

        {step === 1 && (
          <Step1Applicant
            member={member} setMember={setMember}
            purpose={purpose} setPurpose={setPurpose}
            purposeOther={purposeOther} setPurposeOther={setPurposeOther}
            appliedAmount={appliedAmount} setAppliedAmount={setAppliedAmount}
            tenure={tenure} setTenure={setTenure}
            notes={notes} setNotes={setNotes}/>
        )}
        {step === 2 && (
          <Step2Decision
            appliedAmount={appliedAmount}
            decision={decision} setDecision={setDecision}
            approvedAmount={approvedAmount} setApprovedAmount={setApprovedAmount}
            reason={reason} setReason={setReason}/>
        )}
        {step === 3 && (
          <Step3Gold
            items={goldItems} setItems={setGoldItems}
            blank={blankGold}/>
        )}
        {step === 4 && (
          <Step4Guarantors
            applicant={member}
            approved={approvedNum}
            items={guarantors} setItems={setGuarantors}
            blank={blankGuarantor}
            total={guarantorTotal} matches={guarantorMatches}/>
        )}
        {step === 5 && (
          <Step5Repayments
            applicant={member}
            approved={approvedNum}
            tenure={Number(tenure) || 0}
            decision={decision}
            items={repayments} setItems={setRepayments}
            blank={blankRepayment}
            total={repaymentTotal} matches={repaymentMatches}/>
        )}
        {step === 6 && (
          <Step6Payment
            appId={appId}
            applicant={member}
            approved={approvedNum}
            decision={decision}
            payouts={payouts} setPayouts={setPayouts}
            blankPayout={blankPayout}
            rotations={rotations} setRotations={setRotations}
            pool={rotationPool} setPool={setRotationPool}
            payoutTotal={payoutTotal} rotationTotal={rotationTotal}
            paymentTotal={paymentTotal} matches={paymentMatches}/>
        )}
        {step === 7 && (
          <Step7Documents appId={appId} decision={decision}/>
        )}

        {err && <div style={{padding:'10px 14px',background:'var(--bg-sunken)',color:'var(--neg)',borderRadius:6,fontSize:13}}>{err}</div>}
        {info && <div style={{padding:'10px 14px',background:'var(--bg-sunken)',color:'var(--pos)',borderRadius:6,fontSize:13}}>{info}</div>}

        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          {step > 1 && <button className="btn ghost" onClick={onBack} disabled={busy}><I.ArrowRight s={11} style={{transform:'rotate(180deg)'}}/> Back</button>}
          <div style={{flex:1}}/>
          <button className="btn" onClick={onSaveDraft} disabled={busy}>Save as Draft</button>
          {step < 7 && <button className="btn primary" onClick={onNext} disabled={busy}>{busy ? 'Saving…' : 'Next →'}</button>}
          {step === 7 && (
            <button className="btn primary" onClick={onSubmit} disabled={busy || !canSubmit}>
              <I.Check/> {busy ? 'Submitting…' : (decision === 'reject' ? 'Submit (reject)' : 'Submit (approve)')}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ─────────── Step 1: Applicant ───────────
// Canonical purpose list (mirrors server's AllowedPurposes). Order is the
// product-preferred display order — not alphabetical — so "Marriage" and
// "Education" surface before the lower-volume options.
const PURPOSES = [
  { key: 'marriage',        label: 'Marriage' },
  { key: 'education',       label: 'Education' },
  { key: 'medical',         label: 'Medical' },
  { key: 'housing',         label: 'Housing' },
  { key: 'business',        label: 'Business' },
  { key: 'debt_settlement', label: 'Debt Settlement' },
  { key: 'deeni',           label: 'Deeni' },
  { key: 'other',           label: 'Other' },
];

function Step1Applicant({ member, setMember, purpose, setPurpose, purposeOther, setPurposeOther, appliedAmount, setAppliedAmount, tenure, setTenure, notes, setNotes }) {
  return (
    <div className="card">
      <div className="card-header">Applicant & request</div>
      <div className="card-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        <window.MemberSearchPicker value={member} onChange={setMember}/>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
          <div className="field"><label>Purpose</label>
            <select className="select" value={purpose} onChange={e => setPurpose(e.target.value)}>
              {PURPOSES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          <div className="field"><label>Applied amount (BHD)</label>
            <input className="input font-mono" value={appliedAmount} onChange={e => setAppliedAmount(e.target.value)} style={{fontSize:16,fontWeight:600}}/>
          </div>
          <div className="field"><label>Tenure (months)</label>
            <input className="input font-mono" value={tenure} onChange={e => setTenure(e.target.value.replace(/\D+/g,''))}/>
          </div>
        </div>
        {purpose === 'other' && (
          <div className="field">
            <label>Other — please specify</label>
            <input className="input" value={purposeOther} onChange={e => setPurposeOther(e.target.value)}
                   placeholder="Briefly describe the purpose" maxLength={255}/>
          </div>
        )}
        <div className="field"><label>Notes</label>
          <textarea className="textarea" rows={3} value={notes} onChange={e => setNotes(e.target.value)}/>
        </div>
      </div>
    </div>
  );
}

// ─────────── Step 2: Decision ───────────
function Step2Decision({ appliedAmount, decision, setDecision, approvedAmount, setApprovedAmount, reason, setReason }) {
  // Helpful default: when approved is empty, pre-fill it with the
  // applied amount so the common-case "approve as applied" is one click.
  const seedApproved = () => { if (!approvedAmount) setApprovedAmount(appliedAmount); };
  return (
    <div className="card">
      <div className="card-header">Officer decision</div>
      <div className="card-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        <div style={{display:'flex',gap:8}}>
          <button onClick={() => { setDecision('approve'); seedApproved(); }} className={`btn ${decision === 'approve' ? 'accent' : ''}`} style={{flex:1,height:40,justifyContent:'center'}}><I.Check s={13}/> Approve</button>
          <button onClick={() => setDecision('reject')} className={`btn ${decision === 'reject' ? 'accent' : ''}`} style={{flex:1,height:40,justifyContent:'center'}}><I.X s={13}/> Reject</button>
        </div>
        {decision === 'approve' && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="field"><label>Applied amount (BHD)</label>
              <input className="input font-mono" value={appliedAmount} readOnly/>
            </div>
            <div className="field"><label>Approved amount (BHD)</label>
              <input className="input font-mono" value={approvedAmount} onChange={e => setApprovedAmount(e.target.value)} onFocus={seedApproved} style={{fontSize:16,fontWeight:600}}/>
              <div className="hint">Defaults to the applied amount; edit if the committee approved a different figure.</div>
            </div>
          </div>
        )}
        {decision === 'reject' && (
          <div className="field"><label>Reason for rejection</label>
            <textarea className="textarea" rows={3} value={reason} onChange={e => setReason(e.target.value)}/>
            <div className="hint">The wizard skips ahead to the submit step on reject — no security or cheques required.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────── Step 3: Gold collateral ───────────
function Step3Gold({ items, setItems, blank }) {
  const totalWeight = items.reduce((s, it) => s + (Number(it.weight) || 0), 0);
  const totalValue = items.reduce((s, it) => s + (Number(it.estimatedValue) || 0), 0);
  const set = (idx, patch) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  return (
    <div className="card">
      <div className="card-header">Gold security <span className="actions"><span className="muted text-xs">free-text karat — type whatever matches the receipt</span></span></div>
      <div className="card-body" style={{display:'flex',flexDirection:'column',gap:8}}>
        <div className="table-wrap" style={{border:'none'}}>
          <table className="data">
            <thead>
              <tr>
                <th style={{width:120}}>Karat</th>
                <th style={{width:130}} className="right">Weight (g)</th>
                <th>Description</th>
                <th style={{width:160}} className="right">Est. value (BHD)</th>
                <th style={{width:40}}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td><input className="input font-mono" value={it.karat} placeholder="22K" onChange={e => set(i, { karat: e.target.value })}/></td>
                  <td><input className="input font-mono right" value={it.weight} onChange={e => set(i, { weight: e.target.value })}/></td>
                  <td><input className="input" value={it.description} placeholder="e.g. necklace + 2 bangles" onChange={e => set(i, { description: e.target.value })}/></td>
                  <td><input className="input font-mono right" value={it.estimatedValue} onChange={e => set(i, { estimatedValue: e.target.value })}/></td>
                  <td><button className="icon-btn" title="Remove" onClick={() => setItems(prev => prev.filter((_, j) => j !== i))} disabled={items.length === 1}><I.X s={12}/></button></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{background:'var(--bg-sunken)'}}>
                <td className="text-xs muted" style={{fontStyle:'italic'}}>Total</td>
                <td className="num right font-mono" style={{fontWeight:700}}>{totalWeight.toFixed(3)}</td>
                <td className="text-xs muted" style={{fontStyle:'italic'}}>(estimated value is informational only)</td>
                <td className="num right font-mono" style={{fontWeight:700}}>{totalValue.toFixed(3)}</td>
                <td/>
              </tr>
            </tfoot>
          </table>
        </div>
        <div>
          <button className="btn sm" onClick={() => setItems(prev => [...prev, blank()])}><I.Plus s={11}/> Add gold item</button>
        </div>
      </div>
    </div>
  );
}

// ─────────── Step 4: Guarantor cheques ───────────
function Step4Guarantors({ applicant, approved, items, setItems, blank, total, matches }) {
  const set = (idx, patch) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const delta = total - approved;
  return (
    <div className="card">
      <div className="card-header">Guarantor cheques · total must equal approved amount</div>
      <div className="card-body" style={{display:'flex',flexDirection:'column',gap:8}}>
        {items.map((it, i) => (
          <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 32px',gap:8,alignItems:'flex-end',padding:8,background:'var(--bg-sunken)',borderRadius:6}}>
            <div className="field" style={{margin:0}}>
              <label>Guarantor (member)</label>
              <window.MemberSearchPicker
                value={it.guarantor}
                onChange={(m) => {
                  if (m && applicant && m.itsNo === applicant.itsNo) {
                    alert('Applicant cannot be their own guarantor.');
                    return;
                  }
                  set(i, { guarantor: m });
                }}/>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Amount (BHD)</label>
              <input className="input font-mono right" value={it.amount} onChange={e => set(i, { amount: e.target.value })}/>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Cheque #</label>
              <input className="input font-mono" value={it.chequeNumber} onChange={e => set(i, { chequeNumber: e.target.value })}/>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Bank</label>
              <input className="input" value={it.bank} placeholder="Bank name" onChange={e => set(i, { bank: e.target.value })}/>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Cheque date</label>
              <input className="input font-mono" type="date" value={it.chequeDate} onChange={e => set(i, { chequeDate: e.target.value })}/>
            </div>
            <button className="icon-btn" title="Remove" onClick={() => setItems(prev => prev.filter((_, j) => j !== i))} disabled={items.length === 1}><I.X s={12}/></button>
          </div>
        ))}
        <div>
          <button className="btn sm" onClick={() => setItems(prev => [...prev, blank()])}><I.Plus s={11}/> Add guarantor</button>
        </div>
        <ValidationStrip approved={approved} total={total} delta={delta} matches={matches} label="Guarantor cheque total"/>
      </div>
    </div>
  );
}

// ─────────── Step 5: Repayment cheques ───────────
function Step5Repayments({ applicant, approved, tenure, decision, items, setItems, blank, total, matches }) {
  if (decision === 'reject') {
    return (
      <div className="card">
        <div className="card-header">Submit rejection</div>
        <div className="card-body">
          <div className="text-sm muted">No security or cheque schedule needed for a rejected application. Click <b>Submit (reject)</b> to finalise.</div>
        </div>
      </div>
    );
  }
  const set = (idx, patch) => setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const delta = total - approved;
  // Cheque count is capped by the approved tenure: one cheque per
  // month at most. Per-cheque amounts can still vary as long as the
  // total matches the approved amount.
  const tenureCap = tenure > 0 ? tenure : null;
  const atCap = tenureCap != null && items.length >= tenureCap;
  // Quick "Add Next Cheque": copy issuer/bank/amount from the last row,
  // bump the cheque number's trailing digits by 1, and roll the due
  // date forward by one calendar month. Lets the operator key in 12+
  // monthly instalments without retyping common fields.
  const addNext = () => {
    if (atCap) return;
    setItems(prev => {
      const last = prev[prev.length - 1];
      if (!last) return [...prev, blank()];
      return [...prev, {
        issuerName: last.issuerName || '',
        cheque: bumpChequeNumber(last.cheque),
        bank: last.bank || '',
        amount: last.amount || '',
        dueDate: addOneMonth(last.dueDate),
      }];
    });
  };
  const addBlank = () => {
    if (atCap) return;
    setItems(prev => [...prev, blank()]);
  };
  return (
    <div className="card">
      <div className="card-header">Repayment cheques · total must equal approved amount</div>
      <div className="card-body" style={{display:'flex',flexDirection:'column',gap:8}}>
        {items.map((it, i) => (
          <div key={i} style={{display:'grid',gridTemplateColumns:'1.4fr 1fr 1fr 1fr 1fr 32px',gap:8,alignItems:'flex-end',padding:8,background:'var(--bg-sunken)',borderRadius:6}}>
            <div className="field" style={{margin:0}}>
              <label>Issuer name</label>
              <input className="input" value={it.issuerName} placeholder={applicant?.fullName || 'Defaults to applicant'} onChange={e => set(i, { issuerName: e.target.value })}/>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Cheque #</label>
              <input className="input font-mono" value={it.cheque} onChange={e => set(i, { cheque: e.target.value })}/>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Bank</label>
              <input className="input" value={it.bank} placeholder="Bank name" onChange={e => set(i, { bank: e.target.value })}/>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Amount (BHD)</label>
              <input className="input font-mono right" value={it.amount} onChange={e => set(i, { amount: e.target.value })}/>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Due date</label>
              <input className="input font-mono" type="date" value={it.dueDate} onChange={e => set(i, { dueDate: e.target.value })}/>
            </div>
            <button className="icon-btn" title="Remove" onClick={() => setItems(prev => prev.filter((_, j) => j !== i))} disabled={items.length === 1}><I.X s={12}/></button>
          </div>
        ))}
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <button className="btn sm" onClick={addBlank} disabled={atCap}><I.Plus s={11}/> Add cheque</button>
          <button className="btn sm primary" onClick={addNext} disabled={atCap} title="Copy the last cheque, bump the cheque # by 1 and the due date by one month">
            <I.Plus s={11}/> Add next cheque
          </button>
          <span className={`tag ${atCap ? 'warn' : ''}`} style={{marginLeft:'auto'}}>
            {items.length}{tenureCap != null ? ` of ${tenureCap}` : ''} cheque{items.length === 1 ? '' : 's'}
            {atCap ? ' · tenure cap reached' : ''}
          </span>
        </div>
        <ValidationStrip approved={approved} total={total} delta={delta} matches={matches} label="Repayment cheque total"/>
      </div>
    </div>
  );
}

// ─────────── Step 6: Approved-amount payment ───────────
function Step6Payment({ appId, applicant, approved, decision,
                       payouts, setPayouts, blankPayout,
                       rotations, setRotations, pool, setPool,
                       payoutTotal, rotationTotal, paymentTotal, matches }) {
  const banks = (window.QH_DATA?.banks || []).filter(b => b.active);

  // Load the rotation pool whenever the wizard reaches this step. We
  // re-fetch on every entry because another officer might have rotated
  // (or cleared) a cheque while this draft was open.
  useEffectFW(() => {
    if (!appId) { setPool([]); return; }
    let cancel = false;
    window.QH_API.get(`/finance/applications/${appId}/rotation-pool`)
      .then(rows => { if (!cancel) setPool(rows || []); })
      .catch(() => { if (!cancel) setPool([]); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  if (decision === 'reject') {
    return (
      <div className="card">
        <div className="card-header">Approved-amount payment</div>
        <div className="card-body">
          <div className="text-sm muted">Rejected applications don't carry a disbursement payment. Continue to the documents step.</div>
        </div>
      </div>
    );
  }
  if (!approved || approved <= 0) {
    return (
      <div className="card">
        <div className="card-header">Approved-amount payment</div>
        <div className="card-body">
          <div className="text-sm muted">Set an approved amount in step 2 before planning the payment.</div>
        </div>
      </div>
    );
  }

  // ── Section A handlers ─────────────────────────────────────────────
  const setP = (idx, patch) => setPayouts(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  const addPayout = () => setPayouts(prev => [...prev, blankPayout()]);
  const removePayout = (idx) => setPayouts(prev => prev.filter((_, i) => i !== idx));

  // ── Section B handlers ─────────────────────────────────────────────
  // Available pool = server pool, minus anything already in `rotations`
  // (so re-rendering during selection doesn't double-list a cheque).
  const selectedIds = new Set(rotations.map(r => r.sourceRepaymentId));
  const [poolSel, setPoolSel] = useStateFW(new Set()); // checkbox ticks on the left side
  const [rotSel, setRotSel] = useStateFW(new Set());   // checkbox ticks on the right side
  const today = new Date().toISOString().slice(0, 10);
  const moveToRotations = () => {
    const picks = pool.filter(r => poolSel.has(r.id) && !selectedIds.has(r.id));
    if (!picks.length) return;
    setRotations(prev => [...prev, ...picks.map(r => ({
      sourceRepaymentId: r.id,
      applicant: r.applicant,
      cheque: r.cheque,
      bank: r.bank,
      dueDate: r.dueDate,
      amount: Number(r.amount),
      rotatedOn: today,
    }))]);
    setPoolSel(new Set());
  };
  const moveBackToPool = () => {
    setRotations(prev => prev.filter(r => !rotSel.has(r.sourceRepaymentId)));
    setRotSel(new Set());
  };
  const togglePool = (id) => setPoolSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleRot = (id) => setRotSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const fmtTag = (row) => {
    if (row.overdue) return <span className="tag neg" style={{textTransform:'none'}}>OVERDUE</span>;
    // Due within 7 days = "due"; else "upcoming"
    const due = new Date(row.dueDate);
    const diff = (due - new Date()) / (1000 * 60 * 60 * 24);
    if (diff <= 7) return <span className="tag warn" style={{textTransform:'none'}}>DUE</span>;
    return <span className="tag" style={{textTransform:'none'}}>UPCOMING</span>;
  };

  const delta = paymentTotal - approved;
  const visiblePool = pool.filter(r => !selectedIds.has(r.id));

  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      {/* ── Section A: Payment from bank ──────────────────────── */}
      <div className="card">
        <div className="card-header">
          Section A · Payment from bank
          <span className="actions"><span className="muted text-xs">cash, cheque or transfer · pick a bank · no limit on rows</span></span>
        </div>
        <div className="card-body" style={{display:'flex',flexDirection:'column',gap:8}}>
          {payouts.length === 0 && (
            <div className="text-xs muted" style={{padding:'6px 2px'}}>No bank payouts yet. Click "Add row" to include one.</div>
          )}
          {payouts.map((p, i) => (
            <div key={i} style={{display:'grid',gridTemplateColumns:'1.4fr 1fr 1fr 1.6fr 1fr 32px',gap:8,alignItems:'flex-end',padding:8,background:'var(--bg-sunken)',borderRadius:6}}>
              <div className="field" style={{margin:0}}>
                <label>Bank</label>
                <select className="select" value={p.bankId} onChange={e => setP(i, { bankId: e.target.value })}>
                  <option value="">— Select —</option>
                  {banks.map(b => <option key={b.id} value={b.id}>{b.name}{b.code ? ` · ${b.code}` : ''}</option>)}
                </select>
              </div>
              <div className="field" style={{margin:0}}>
                <label>Cheque # / ref</label>
                <input className="input font-mono" value={p.reference} placeholder="e.g. 00123 or 'transfer'"
                  onChange={e => setP(i, { reference: e.target.value })}/>
              </div>
              <div className="field" style={{margin:0}}>
                <label>Cheque date</label>
                <input className="input font-mono" type="date" value={p.issueDate}
                  onChange={e => setP(i, { issueDate: e.target.value })}/>
              </div>
              <div className="field" style={{margin:0}}>
                <label>Description</label>
                <input className="input" value={p.description} placeholder="e.g. payable to applicant"
                  onChange={e => setP(i, { description: e.target.value })}/>
              </div>
              <div className="field" style={{margin:0}}>
                <label>Amount (BHD)</label>
                <input className="input font-mono right" value={p.amount}
                  onChange={e => setP(i, { amount: e.target.value })}/>
              </div>
              <button className="icon-btn" title="Remove" onClick={() => removePayout(i)}><I.X s={12}/></button>
            </div>
          ))}
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button className="btn sm" onClick={addPayout}><I.Plus s={11}/> Add row</button>
            <span className="muted text-xs" style={{marginLeft:'auto'}}>Section A total · <b className="font-mono">{payoutTotal.toFixed(3)}</b> BHD</span>
          </div>
        </div>
      </div>

      {/* ── Section B: Rotated repayment cheques ──────────────── */}
      <div className="card">
        <div className="card-header">
          Section B · Rotate uncleared repayment cheques
          <span className="actions"><span className="muted text-xs">hand an existing cheque to this applicant instead of writing a fresh one</span></span>
        </div>
        <div className="card-body" style={{display:'flex',flexDirection:'column',gap:8}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr auto 1fr',gap:8,alignItems:'stretch'}}>
            <div style={{display:'flex',flexDirection:'column',gap:6,background:'var(--bg-sunken)',borderRadius:6,padding:8,maxHeight:340,overflow:'auto'}}>
              <div className="text-xs uppercase muted">Available cheques ({visiblePool.length})</div>
              {visiblePool.length === 0 && <div className="text-xs muted" style={{padding:'6px 2px'}}>No uncleared cheques from other applications.</div>}
              {visiblePool.map(r => (
                <label key={r.id} style={{display:'flex',alignItems:'center',gap:8,padding:6,background:'var(--bg-elev)',borderRadius:4,cursor:'pointer',fontSize:12}}>
                  <input type="checkbox" checked={poolSel.has(r.id)} onChange={() => togglePool(r.id)}/>
                  <span style={{flex:1,minWidth:0}}>
                    <span style={{fontWeight:600}}>{r.applicant}</span> {r.memberItsNo && <span className="muted">· ITS {r.memberItsNo}</span>}
                    <br/>
                    <span className="muted font-mono">{r.bank} · #{r.cheque} · due {window.fmtDate(r.dueDate)}</span>
                  </span>
                  <span className="font-mono" style={{fontWeight:600}}>{Number(r.amount).toFixed(3)}</span>
                  {fmtTag(r)}
                </label>
              ))}
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:6,justifyContent:'center',padding:'0 4px'}}>
              <button className="btn sm" onClick={moveToRotations} disabled={poolSel.size === 0} title="Move selected to rotations">
                <I.ArrowRight s={11}/>
              </button>
              <button className="btn sm" onClick={moveBackToPool} disabled={rotSel.size === 0} title="Move back to pool">
                <I.ArrowRight s={11} style={{transform:'rotate(180deg)'}}/>
              </button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:6,background:'var(--bg-sunken)',borderRadius:6,padding:8,maxHeight:340,overflow:'auto'}}>
              <div className="text-xs uppercase muted">Selected for this disbursement ({rotations.length})</div>
              {rotations.length === 0 && <div className="text-xs muted" style={{padding:'6px 2px'}}>None selected. Tick rows on the left and click → to move.</div>}
              {rotations.map(r => (
                <label key={r.sourceRepaymentId} style={{display:'flex',alignItems:'center',gap:8,padding:6,background:'var(--bg-elev)',borderRadius:4,cursor:'pointer',fontSize:12}}>
                  <input type="checkbox" checked={rotSel.has(r.sourceRepaymentId)} onChange={() => toggleRot(r.sourceRepaymentId)}/>
                  <span style={{flex:1,minWidth:0}}>
                    <span style={{fontWeight:600}}>{r.applicant}</span><br/>
                    <span className="muted font-mono">{r.bank} · #{r.cheque} · due {window.fmtDate(r.dueDate)}</span>
                  </span>
                  <span className="font-mono" style={{fontWeight:600}}>{Number(r.amount).toFixed(3)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="text-xs muted" style={{textAlign:'right'}}>Section B total · <b className="font-mono">{rotationTotal.toFixed(3)}</b> BHD</div>
        </div>
      </div>

      {/* ── Combined totals validator ─────────────────────────── */}
      <div style={{padding:'10px 14px',background: matches ? 'var(--bg-sunken)' : 'var(--bg-elev)',borderRadius:6,display:'flex',alignItems:'center',gap:14,fontSize:13,border: matches ? '1px solid var(--pos)' : '1px solid var(--warn)'}}>
        <span><span className="muted text-xs uppercase">Approved </span><b>{approved.toFixed(3)} BHD</b></span>
        <span>=</span>
        <span><span className="muted text-xs uppercase">A bank </span><b className="font-mono">{payoutTotal.toFixed(3)}</b></span>
        <span>+</span>
        <span><span className="muted text-xs uppercase">B rotated </span><b className="font-mono">{rotationTotal.toFixed(3)}</b></span>
        <span>=</span>
        <span><b className="font-mono">{paymentTotal.toFixed(3)} BHD</b></span>
        <span className={`tag ${matches ? 'pos' : 'warn'}`} style={{marginLeft:'auto'}}>
          {matches ? 'Matched' : delta > 0 ? `over by ${delta.toFixed(3)}` : `short by ${(-delta).toFixed(3)}`}
        </span>
      </div>
    </div>
  );
}

// ─────────── Step 7: Attach documents ───────────
function Step7Documents({ appId, decision }) {
  // Uploads are live — they hit the server immediately. No staging
  // because by step 6 the application id always exists (created in
  // step 1's draft save). Documents persist across draft / submit /
  // reopen, so the operator can come back later from the Finance Apps
  // list to add or remove attachments.
  if (!appId) {
    return (
      <div className="card">
        <div className="card-header">Attach documents</div>
        <div className="card-body">
          <div className="text-sm muted">Save the application first (any earlier step) before attaching documents.</div>
        </div>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="card-header">
        Attach documents
        <span className="actions"><span className="muted text-xs">optional · {decision === 'reject' ? 'rejection paperwork' : 'application + collateral paperwork'}</span></span>
      </div>
      <div className="card-body" style={{display:'flex',flexDirection:'column',gap:10}}>
        <div className="text-sm muted">
          Upload supporting documents (member ID, gold receipts, cheque scans, signed undertaking, etc.).
          Files are stored under the configured documents path and remain attached to this application
          across edits, submission, and admin reopens.
        </div>
        <window.DocumentAttachments
          entityType="finance_app"
          entityId={appId}
          editable={true}
          emptyMsg="No documents attached yet — pick one or more files to upload."/>
      </div>
    </div>
  );
}

// Bump the trailing-digit segment of a cheque number, preserving any
// non-numeric prefix and zero-padding. "125" → "126", "CHQ-100" →
// "CHQ-101", "ABC0099" → "ABC0100". Returns '' when there's nothing
// numeric to increment.
function bumpChequeNumber(s) {
  if (!s) return '';
  const m = String(s).match(/^(.*?)(\d+)(\D*)$/);
  if (!m) return '';
  const [, prefix, num, suffix] = m;
  const next = String(Number(num) + 1).padStart(num.length, '0');
  return prefix + next + suffix;
}

// Add one calendar month to an ISO YYYY-MM-DD date, clamping the day
// to the last day of the new month so Jan 31 + 1mo = Feb 28/29 instead
// of overflowing into March.
function addOneMonth(dateStr) {
  if (!dateStr) return '';
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const [, y, mo, d] = m;
  const year = +y, month = +mo - 1, day = +d;
  const newMonthIdx = month + 1;
  const newYear = year + Math.floor(newMonthIdx / 12);
  const newMonth = ((newMonthIdx % 12) + 12) % 12;
  const lastDay = new Date(newYear, newMonth + 1, 0).getDate();
  const newDay = Math.min(day, lastDay);
  const pad = (n) => String(n).padStart(2, '0');
  return `${newYear}-${pad(newMonth + 1)}-${pad(newDay)}`;
}

function ValidationStrip({ approved, total, delta, matches, label }) {
  const tone = matches ? 'pos' : 'warn';
  const sign = delta > 0 ? `over by ${delta.toFixed(3)}` : delta < 0 ? `short by ${(-delta).toFixed(3)}` : 'matched';
  return (
    <div style={{padding:'10px 14px',background: matches ? 'var(--bg-sunken)' : 'var(--bg-elev)',borderRadius:6,display:'flex',alignItems:'center',gap:14,fontSize:13,border: matches ? '1px solid var(--pos)' : '1px solid var(--warn)'}}>
      <span><span className="muted text-xs uppercase">Approved </span><b>{approved.toFixed(3)} BHD</b></span>
      <span><span className="muted text-xs uppercase">{label} </span><b>{total.toFixed(3)} BHD</b></span>
      <span className={`tag ${tone}`}>{matches ? 'Matched' : sign}</span>
    </div>
  );
}

window.FinanceWizardPage = FinanceWizardPage;
